import * as React from 'react';

import {DraggableList} from 'shells/draggable-row/draggable-list';
// @ts-expect-error - imported from .js/.jsx file
import {PencilToPaperIcon} from 'shells/icon/svg/pencil-to-paper-icon';
import {AddButtonRow} from 'shells/table/table-body/add-button-row';

import type {CustomFieldFragment as CustomField} from 'nutshell-graphql-types';

import {LegacySetupTableWrapper} from '../../../client_modules/shells/react/components/table/legacy-setup-table-wrapper';

import {TAB_GRAPHQL_MAP, LEAD_TAB_NAME, TAB_TITLE_MAP} from './custom-fields';
import {CustomFieldRowItem} from './custom-fields-row-item';
import {useUpdateCustomFieldPositions} from './graphql/hooks';
import type {ListItemStat} from './types';

import styles from './custom-fields.module.css';

const POSITION_OFFSET = 1000;

type Props = {
    customFields: CustomField[];
    entityType: string;
    onOpenCreateModal: () => void;
    onOpenEditModal: (fieldId: string) => void;
    onArchive: (fieldIdToArchive: string) => void;
    onRestore: (fieldIdToRestore: string) => void;
    isViewingArchived: boolean;
};

export function CustomFieldsTable(props: Props) {
    const [items, setItems] = React.useState([]);

    const {updateCustomFieldPositions} = useUpdateCustomFieldPositions();

    const isDisabled = React.useMemo(() => {
        if (NutClientConfig.hasPeepPagesRedesignFF) {
            return true;
        }
        return props.entityType === LEAD_TAB_NAME || props.isViewingArchived;
    }, [props.entityType, props.isViewingArchived]);

    React.useEffect(() => {
        const newItems = [];
        for (const field of props.customFields) {
            const stats = field.customFieldStats;
            const numOfForms = stats && stats.numOfForms ? stats.numOfForms : 0;

            const statsList: Array<ListItemStat> = [
                {
                    id: `forms-${field.attribute.displayName}`,
                    count: numOfForms,
                    icon: PencilToPaperIcon,
                    tooltipText: `This field is used in ${numOfForms} form${
                        numOfForms === 1 ? '' : 's'
                    }`,
                },
            ];

            newItems.push({
                id: field.id,
                component: (
                    <CustomFieldRowItem
                        displayName={field.attribute.displayName}
                        fieldType={field.attribute.displayType}
                        description={field.attribute.description}
                        onArchive={() => props.onArchive(field.id)}
                        onEdit={() => props.onOpenEditModal(field.id)}
                        isViewingArchived={props.isViewingArchived}
                        onRestore={() => props.onRestore(field.id)}
                        isOnPipelineTable={false}
                        fieldIsShownInPipeline={() => false}
                        onReorderField={() => {}}
                        stats={statsList}
                    />
                ),
                isDisabled,
            });
        }
        // @ts-expect-error
        setItems(newItems);
    }, [isDisabled, props]);

    const handleReorder = (startIndex: number, endIndex: number) => {
        const newItems = [...items];
        const [reorderedItem] = newItems.splice(startIndex, 1);
        // @ts-expect-error
        newItems.splice(endIndex, 0, reorderedItem);

        setItems(newItems);

        const fieldPositions = newItems.map((item, index) => {
            return {
                // @ts-expect-error
                fieldId: item.id,
                position: getCustomFieldPosition(index),
            };
        });

        updateCustomFieldPositions({
            fieldPositions: fieldPositions,
            // @ts-expect-error
            entityType: TAB_GRAPHQL_MAP[props.entityType],
        });
    };

    function getCustomFieldPosition(index: number) {
        return index + POSITION_OFFSET;
    }

    return (
        <>
            <div className='full-width'>
                {/* @ts-expect-error */}
                <LegacySetupTableWrapper title={`${TAB_TITLE_MAP[props.entityType]} custom fields`}>
                    {!props.isViewingArchived && (
                        <AddButtonRow
                            buttonText='Create a new field'
                            onClick={props.onOpenCreateModal}
                        />
                    )}
                    {props.customFields.length > 0 ? (
                        <div className='flex flex-dir-col gap-16 flex-grow mt-8'>
                            <DraggableList
                                listItems={items}
                                handleReorder={handleReorder}
                                disabled={isDisabled}
                            />
                        </div>
                    ) : (
                        <div className={styles['table-empty-section']}>
                            {props.isViewingArchived ? (
                                <div>
                                    You have not archived any {props.entityType.toLowerCase()}{' '}
                                    custom fields yet.
                                </div>
                            ) : (
                                <div>
                                    You have not created any {props.entityType.toLowerCase()} custom
                                    fields yet.
                                </div>
                            )}
                        </div>
                    )}
                </LegacySetupTableWrapper>
            </div>
        </>
    );
}
